import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Loader,
  Table,
  Center,
  Text,
  Paper,
  Group,
  ActionIcon,
  Code,
  Badge,
  Box,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { getTenantApikeys, deleteApiKey } from "../api";
import { IconTrash } from "@tabler/icons-react";
import { ApiKeyCreateForm } from "./ApiKeyCreate";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import Timeago from "../_Shared/Timeago";

export default function ApiKeys({ additionalData, brandName, brands }) {
  const [apiKeys, setApiKeys] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();

  useEffect(() => {
    getTenantApikeys().then((data) => {
      setApiKeys(data);
    });
  }, []);

  return (
    <Box mt="xs">
      <Modal
        size="lg"
        closeOnClickOutside={false}
        opened={opened}
        p="xl"
        onClose={close}
        title={t("tenant.api_key_modal_title", "API Key")}
      >
        <ApiKeyCreateForm
          additionalData={additionalData}
          brandName={brandName}
          brands={brands}
          keyAdded={(newKey) => {
            setApiKeys([newKey, ...apiKeys]);
          }}
        />
      </Modal>

      <Group justify="space-between">
        <Text mb="sm" fz="xl" fw={500}>
          {t("tenant.api_keys_title", "API Keys")}
        </Text>

        <Button variant="outline" onClick={() => open()}>
          {t("tenant.api_key_create_new", "Create new key")}
        </Button>
      </Group>

      {!apiKeys ? (
        <Center>
          <Loader size="xl" mt="xl" />
        </Center>
      ) : (
        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("tenant.api_key_col_name", "Name")}</Table.Th>
              {brands && <Table.Th>{t("tenant.api_key_col_brand", "Brand")}</Table.Th>}
              <Table.Th>{t("tenant.api_key_col_permission", "Permission")}</Table.Th>
              <Table.Th>{t("tenant.api_key_col_last4", "Last 4")}</Table.Th>
              <Table.Th>{t("tenant.api_key_col_issued", "Issued")}</Table.Th>
              <Table.Th>{t("tenant.api_key_col_last_used", "Last Used")}</Table.Th>
              <Table.Th>{t("tenant.api_key_col_expires", "Expires")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {apiKeys.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td>{p.name}</Table.Td>
                {brands && (
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      {brands.find((b) => b.id === p.brandId)?.name ??
                        p.brandId ??
                        t("tenant.api_key_all_brands", "All")}
                    </Badge>
                  </Table.Td>
                )}
                <Table.Td>
                  {p.permission ? (
                    <Badge
                      variant="light"
                      size="sm"
                      color={p.permission === "readonly" ? "green" : p.permission === "admin" ? "red" : "yellow"}
                    >
                      {p.permission === "readonly"
                        ? t("tenant.api_key_permission_readonly", "Read-only")
                        : p.permission === "readwrite"
                          ? t("tenant.api_key_permission_readwrite", "Read & write")
                          : t("tenant.api_key_permission_admin", "Admin")}
                    </Badge>
                  ) : (
                    <Text c="dimmed" size="sm">
                      {t("tenant.api_key_permission_legacy", "Legacy (unrestricted)")}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Code fz="xs">{p.last4}</Code>
                </Table.Td>
                <Table.Td>
                  <Timeago date={p.issuedAt ?? p.created} />
                </Table.Td>
                <Table.Td>
                  <Timeago date={p.lastUsed} />
                </Table.Td>
                <Table.Td>
                  <Timeago date={p.expiresAt} allowFuture={true} />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="red"
                    variant="outline"
                    onClick={() =>
                      deleteApiKey(p.id).then(() => {
                        setApiKeys(apiKeys.filter((q) => q.id != p.id));
                        notifications.show({ title: t("tenant.api_key_deleted", "API key deleted") });
                      })
                    }
                  >
                    <IconTrash size="1rem" />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Box>
  );
}
